/**
 * The Live Promote views' rules, free of rendering: where each prepare step stands, what switching on before Ready
 * does, where a decision came from, the Review filter, History's entries and filters, and each write's summary.
 */

import type { NotReadyItem, PromotionEvent, PromotionEventKind, PromotionJob, PromotionRecord } from '@/modules/promotion/types';
import type { BucketRun } from '@/types/websocket.types';
import type { LivePoolView, LiveTicket, LiveType } from '../types';
import { percentOf, scenarioLabelOf, type ScenarioState } from './scenarios';

export type StepStatus = 'done' | 'active' | 'waiting' | 'failed';

export interface Step {
	title: string;
	detail: string;
	status: StepStatus;
	/** "6 found", "120 of 212". */
	count?: string;
	/** 0 to 1, for the step being worked on. */
	progress?: number;
}

const isRunning = (status: string | undefined): boolean => status === 'queued' || status === 'running';

/** Where each step stands, from the pool, the latest live sort and the latest template generation. */
export const prepareStepsOf = (
	view: LivePoolView,
	run: BucketRun | null,
	job: PromotionJob | null | undefined
): Step[] => {
	const types = view.types.length;
	const writing = view.typeGeneration;
	const sorting = run?.status === 'running';
	const sorted = types > 0 && !sorting && view.pool.open === 0;
	return [
		{
			title: 'Find the scenarios',
			detail: 'One per branch of the protocol. Skipped when back testing already made them.',
			status: types ? 'done' : writing?.status === 'failed' ? 'failed' : writing ? 'active' : 'waiting',
			count: types ? `${types} found` : undefined
		},
		{
			title: 'Sort the live tickets',
			detail: 'Graded Sentinel tickets from the last 30 days, each into the scenario it fits.',
			status: sorted ? 'done' : sorting ? 'active' : 'waiting',
			count: sorting && run ? `${run.done} of ${run.total}` : sorted ? `${view.pool.sorted} sorted` : undefined,
			progress: sorting && run?.total ? run.done / run.total : undefined
		},
		{
			title: 'Write a reply for each scenario',
			detail: 'From its tickets graded Perfect or Good Enough. Each reply starts as a Draft.',
			status: isRunning(job?.status) ? 'active' : job?.status === 'failed' ? 'failed' : 'waiting'
		}
	];
};


/** What switching the item on does, in words. */
export const notReadyEffectOf = (item: NotReadyItem): string =>
	item.tool
		? `Kim will run ${item.tool} without asking a supervisor on "${item.description}". Other scenarios don't change.`
		: `"${item.description}" will send its reply by itself. Other scenarios don't change.`;


/** Where a decision was made, as people call it. */
const SOURCE_LABEL: Record<string, string> = {
	sentinel: 'Sentinel',
	extension: 'extension',
	automation_log: 'Automation logs'
};

export const sourceLabelOf = (source: string | null): string => (source ? (SOURCE_LABEL[source] ?? source) : 'unknown');


export type TicketFilter = 'all' | 'bad' | 'good';

export const isGood = (ticket: LiveTicket): boolean => ticket.grade === 'Perfect' || ticket.grade === 'Good Enough';

export const filterTickets = (tickets: LiveTicket[], filter: TicketFilter): LiveTicket[] =>
	filter === 'all' ? tickets : tickets.filter((ticket) => (filter === 'good' ? isGood(ticket) : !isGood(ticket)));


export type HistoryFilter = 'all' | 'switches' | 'system';

const SYSTEM_KINDS: PromotionEventKind[] = [
	'outdated',
	'write_rejected_while_auto',
	'reply_not_ready_while_auto',
	'reject_alert_sent',
	'outdated_alert_sent'
];

export interface HistoryEntry extends PromotionEvent {
	/** Who promoted, read off the record, for its own events. */
	byName: string | null;
}

/** Every event of every promotion, newest first, each with the name a person was known by. */
export const historyEntriesOf = (records: PromotionRecord[]): HistoryEntry[] =>
	records
		.flatMap((record) =>
			record.events.map((event) => ({
				...event,
				byName:
					event.by === null
						? null
						: event.by === record.promotedBy
							? record.promotedByName
							: event.by === record.endedBy
								? record.endedByName
								: null
			}))
		)
		.sort((a, b) => (a.at < b.at ? 1 : -1));

export const matchesFilter = (entry: HistoryEntry, filter: HistoryFilter): boolean => {
	if (filter === 'all') return true;
	const system = entry.by === null || SYSTEM_KINDS.includes(entry.kind);
	return filter === 'system' ? system : !system;
};


export interface OverviewRow {
	type: LiveType;
	state: ScenarioState;
}

/** Each write of the protocol: how many scenarios reach it, in how many it runs by itself, and its worst reject rate. */
export const writeSummariesOf = (rows: OverviewRow[]) => {
	const tools = [...new Set(rows.flatMap((row) => row.type.writes.map((write) => write.tool)))];
	return tools.map((tool) => {
		const uses = rows.flatMap((row) =>
			row.type.writes.filter((write) => write.tool === tool).map((write) => ({ scenario: scenarioLabelOf(row.type), write }))
		);
		const worst = uses
			.filter((use) => use.write.rejectRate !== null && use.write.rejected > 0)
			.sort((a, b) => (b.write.rejectRate ?? 0) - (a.write.rejectRate ?? 0))[0];
		return {
			tool,
			scenarios: uses.length,
			auto: uses.filter((use) => use.write.mode === 'auto').length,
			rejected: uses.reduce((sum, use) => sum + use.write.rejected, 0),
			worst: worst ? { scenario: worst.scenario, rate: percentOf(worst.write.rejectRate) } : null
		};
	});
};

